import { useContext, useEffect, useRef, useState } from 'react'

import { TargetContext, TargetLookupContext, ViewContext } from './context'

import type { ReactNode } from 'react'
import type { JourneyCoreState } from './types'

interface PhoneJourneyStageProps {
  state: JourneyCoreState
  /** Who the journey is signed in as; shown in the top bar. */
  user: { name: string; role?: string }
  /** A short note under the top bar, e.g. that the person signed in again. */
  badge?: string | null
  children: ReactNode
}

/**
 * The phone counterpart of `JourneyStage`: the same timeline engine and the
 * same target registry, drawn the way Veekls looks on a phone.
 *
 * There is no sidebar, because on a phone the real menu stacks above the page,
 * and there is no cursor. A finger has no resting place, so a dot appears only
 * where a tap lands: a click step names its target in the same patch, and the
 * dot is measured at that moment.
 */
export const PhoneJourneyStage = ({ state, user, badge, children }: PhoneJourneyStageProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const targets = useRef<Record<string, HTMLElement | null>>({})
  const [touch, setTouch] = useState<{ x: number; y: number } | null>(null)

  const registerTarget = useRef((key: string) => (node: HTMLElement | null) => {
    targets.current[key] = node
  }).current

  const lookupTarget = useRef((key: string) => targets.current[key] ?? null).current

  // Read through a ref so the tap effect fires once per click, not again when
  // a later step moves the target without tapping.
  const targetRef = useRef(state.target)

  targetRef.current = state.target

  useEffect(() => {
    const viewport = viewportRef.current
    const node = targetRef.current ? targets.current[targetRef.current] : null

    if (state.clickSeq === 0 || !viewport || !node) return

    const bounds = viewport.getBoundingClientRect()
    const target = node.getBoundingClientRect()

    setTouch({
      x: target.left - bounds.left + target.width / 2,
      y: target.top - bounds.top + target.height / 2,
    })

    const lift = window.setTimeout(() => setTouch(null), 420)

    return () => window.clearTimeout(lift)
  }, [state.clickSeq])

  return (
    <TargetContext.Provider value={registerTarget}>
      <TargetLookupContext.Provider value={lookupTarget}>
        <ViewContext.Provider value={state.view}>
          <div ref={viewportRef} className="vk-sim-viewport vk-phone-viewport">
            {touch && (
              <>
                <div className="vk-touch" data-down={state.clicking} style={{ left: touch.x, top: touch.y }} />
                <span key={state.clickSeq} className="vk-touch-ripple" style={{ left: touch.x, top: touch.y }} />
              </>
            )}

            <div className="absolute inset-x-0 top-0 z-30 flex h-[34px] items-center justify-between border-b border-[#e4e1e4] bg-[#faf8f8] px-2.5">
              <div className="flex items-center gap-1.5">
                <img src="/icons/icon-96.png" alt="" className="h-4 w-4" />
                <span className="text-[11px] font-bold tracking-tight text-[#1c1917]">Veekls</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="vk-persona text-right leading-none" key={user.name}>
                  <p className="text-[9.5px] font-bold text-[#1c1917]">{user.name}</p>
                  {user.role && <p className="mt-0.5 text-[8.5px] text-[#706d71]">{user.role}</p>}
                </div>
                <div className="h-5 w-5 shrink-0 rounded-full bg-stone-300" />
              </div>
            </div>

            <div
              className="absolute right-2 top-[40px] z-40 rounded bg-[#1c1917] px-1.5 py-1 font-mono text-[8.5px] uppercase tracking-wider text-white transition-all duration-300"
              style={{ opacity: badge ? 1 : 0, transform: `translateY(${badge ? 0 : -4}px)` }}
            >
              {badge}
            </div>

            <div className="absolute inset-x-0 bottom-0 top-[34px]">{children}</div>
          </div>
        </ViewContext.Provider>
      </TargetLookupContext.Provider>
    </TargetContext.Provider>
  )
}

interface JourneyPanProps {
  /** Target key to bring into view; null scrolls back to the top. */
  to: string | null
  /** Where the target should sit, as a fraction of the visible height. */
  align?: number
  children: ReactNode
}

/**
 * A screen taller than the phone, scrolled the way a thumb would: the content
 * slides until the target sits at `align`, and a finger traces the swipe.
 * Scrolling back to the top happens without the trace, as a reset.
 */
export const JourneyPan = ({ to, align = 0.5, children }: JourneyPanProps) => {
  const lookupTarget = useContext(TargetLookupContext)
  const boxRef = useRef<HTMLDivElement>(null)
  const panRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(0)
  const [swipeSeq, setSwipeSeq] = useState(0)

  useEffect(() => {
    const box = boxRef.current
    const pan = panRef.current
    const node = to ? lookupTarget(to) : null

    if (!box || !pan || !node) {
      setOffset(0)

      return
    }

    // Both rects move with the transform, so their difference is the target's
    // position in the content whatever the current offset is.
    const nodeRect = node.getBoundingClientRect()
    const centre = nodeRect.top - pan.getBoundingClientRect().top + nodeRect.height / 2
    const max = Math.max(0, pan.offsetHeight - box.clientHeight)

    setOffset(Math.min(max, Math.max(0, centre - box.clientHeight * align)))
    setSwipeSeq((seq) => seq + 1)
  }, [to, align, lookupTarget])

  return (
    <div ref={boxRef} className="absolute inset-0 overflow-hidden">
      <div ref={panRef} className="vk-pan" style={{ transform: `translateY(${-offset}px)` }}>
        {children}
      </div>
      {swipeSeq > 0 && <span key={swipeSeq} className="vk-swipe" />}
    </div>
  )
}
